import { Dictionary } from '../models';
import { context, CookieStorageAdapter, targetingService } from '../services/';
import { logger } from './logger';

export class Targeting {
	constructor(private readonly container: Window) {}

	private static containsValue(valuesList: string[], value: string): boolean {
		const upperValue = value.toUpperCase();
		return valuesList.some((existingBundle) => existingBundle.toUpperCase() === upperValue);
	}

	getHostnamePrefix(): string | undefined {
		const hostname = this.container.location.hostname.toLowerCase();
		const match = /(^|.)(showcase|externaltest|preview|verify|stable|sandbox-[^.]+)\./.exec(
			hostname,
		);

		if (match && match.length > 2) {
			return match[2];
		}

		const pieces = hostname.split('.');

		if (pieces.length) {
			return pieces[0];
		}

		return undefined;
	}

	getRawDbName(wikiDbName: string): string {
		return `_${wikiDbName || 'wikia'}`.replace('/[^0-9A-Z_a-z]/', '_');
	}

	getTargetingBundles(bundles: Dictionary<Dictionary<string[]>>): string[] {
		const targetingBundles = targetingService.get('bundles') || [];

		try {
			const selectedBundles: string[] = [];

			Object.keys(bundles).forEach((key) => {
				if (this.matchesTargetingBundle(bundles[key])) {
					selectedBundles.push(key);
				}
			});

			selectedBundles.forEach((bundle) => {
				if (!Targeting.containsValue(targetingBundles, bundle)) {
					targetingBundles.push(bundle);
				}
			});
		} catch (e) {
			logger('targeting-bundles', 'Invalid input data!');
		}

		return this.applyCodeLevelBundles(targetingBundles);
	}

	private applyCodeLevelBundles(bundles: string[]): string[] {
		const cookieAdapter = new CookieStorageAdapter();
		const shortPageWordsLimit = 100;

		if (cookieAdapter.getItem('_ae_intrsttl_imp')) {
			bundles.push('interstitial_disabled');
		}
		const skin = targetingService.get('skin');

		if (
			skin &&
			skin.includes('ucp_') &&
			!Targeting.containsValue(bundles, 'VIDEO_TIER_1_AND_2_BUNDLE')
		) {
			bundles.push('VIDEO_TIER_3_BUNDLE');
		}

		const wordCount = targetingService.get('word_count') || -1;

		if (wordCount > -1 && wordCount <= shortPageWordsLimit) {
			bundles.push('short_page');
			context.set('custom.short_page', true);
		}

		return bundles;
	}

	private matchesTargetingBundle(bundle: Dictionary<string[]>): boolean {
		return !Object.keys(bundle).some((key) => {
			const acceptedValues = targetingService.get(key);

			if (!acceptedValues) {
				return true;
			}

			if (Array.isArray(acceptedValues)) {
				if (!bundle[key].some((find) => acceptedValues.includes(find))) {
					return true;
				}
			} else {
				if (!bundle[key].includes(acceptedValues)) {
					return true;
				}
			}

			return false;
		});
	}
}

export const targeting = new Targeting(window);
